import { useState, useEffect } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Loader2, Star, Calendar, DollarSign, Clock, CheckCircle, XCircle } from "lucide-react";
import { RatingDialog } from "@/components/influencer/RatingDialog";
import { RatingStars } from "@/components/influencer/RatingStars";

interface BookingWithInfluencer {
  id: string;
  campaign_type: string;
  campaign_description: string | null;
  budget: number | null;
  preferred_start_date: string | null;
  preferred_end_date: string | null;
  status: string;
  created_at: string;
  influencer_notes: string | null;
  influencer: {
    id: string;
    name: string;
    image: string | null;
    category: string;
  };
  has_rating: boolean;
}

export const MyInfluencerBookings = () => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const isRTL = language === 'ar';

  const [loading, setLoading] = useState(true);
  const [bookings, setBookings] = useState<BookingWithInfluencer[]>([]);
  const [ratingDialog, setRatingDialog] = useState<{
    open: boolean;
    bookingId: string;
    influencerId: string;
    influencerName: string;
  }>({ open: false, bookingId: "", influencerId: "", influencerName: "" });

  const fetchBookings = async () => {
    if (!user) return;

    try {
      setLoading(true);

      // Fetch user's bookings with influencer data
      const { data: bookingsData, error } = await supabase
        .from('influencer_bookings')
        .select(`
          id,
          campaign_type,
          campaign_description,
          budget,
          preferred_start_date,
          preferred_end_date,
          status,
          created_at,
          influencer_notes,
          influencer_id
        `)
        .eq('requester_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      if (!bookingsData || bookingsData.length === 0) {
        setBookings([]);
        return;
      }

      // Fetch influencer details
      const influencerIds = [...new Set(bookingsData.map(b => b.influencer_id))];
      const { data: influencersData } = await supabase
        .from('influencers')
        .select('id, name, image, category')
        .in('id', influencerIds);

      // Fetch existing ratings
      const bookingIds = bookingsData.map(b => b.id);
      const { data: ratingsData } = await supabase
        .from('influencer_ratings')
        .select('booking_id')
        .in('booking_id', bookingIds);

      const ratedBookingIds = new Set(ratingsData?.map(r => r.booking_id) || []);

      // Combine data
      const combinedBookings: BookingWithInfluencer[] = bookingsData.map(booking => {
        const influencer = influencersData?.find(i => i.id === booking.influencer_id);
        return {
          ...booking,
          influencer: influencer || { id: booking.influencer_id, name: 'Unknown', image: null, category: 'Unknown' },
          has_rating: ratedBookingIds.has(booking.id),
        };
      });

      setBookings(combinedBookings);
    } catch (error) {
      console.error('Error fetching bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBookings();
  }, [user]);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'accepted':
        return (
          <Badge className="bg-blue-500/10 text-blue-600 border-blue-500/20">
            <CheckCircle className="h-3 w-3 mr-1" />
            {isRTL ? "مقبول" : "Accepted"}
          </Badge>
        );
      case 'completed':
        return (
          <Badge className="bg-green-500/10 text-green-600 border-green-500/20">
            <CheckCircle className="h-3 w-3 mr-1" />
            {isRTL ? "مكتمل" : "Completed"}
          </Badge>
        );
      case 'rejected':
        return (
          <Badge variant="destructive" className="bg-red-500/10 text-red-600 border-red-500/20">
            <XCircle className="h-3 w-3 mr-1" />
            {isRTL ? "مرفوض" : "Rejected"}
          </Badge>
        );
      case 'cancelled':
        return (
          <Badge variant="secondary">
            {isRTL ? "ملغى" : "Cancelled"}
          </Badge>
        );
      default:
        return (
          <Badge variant="secondary" className="bg-yellow-500/10 text-yellow-600 border-yellow-500/20">
            <Clock className="h-3 w-3 mr-1" />
            {isRTL ? "قيد الانتظار" : "Pending"}
          </Badge>
        );
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (bookings.length === 0) {
    return null;
  }

  return (
    <div className="space-y-6 mb-8">
      <h2 className="text-xl font-semibold">
        {isRTL ? "حجوزات المؤثرين" : "My Influencer Bookings"}
      </h2>

      <div className="grid gap-4">
        {bookings.map((booking) => (
          <Card key={booking.id} className="overflow-hidden">
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row gap-4">
                {/* Influencer Info */}
                <div className="flex items-center gap-3 min-w-0">
                  <Avatar className="h-12 w-12">
                    <AvatarImage src={booking.influencer.image || undefined} />
                    <AvatarFallback>
                      {booking.influencer.name?.charAt(0) || 'I'}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0">
                    <h4 className="font-semibold truncate">{booking.influencer.name}</h4>
                    <p className="text-sm text-muted-foreground capitalize">
                      {booking.influencer.category}
                    </p>
                  </div>
                </div>

                {/* Booking Details */}
                <div className="flex-1 space-y-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant="outline">{booking.campaign_type}</Badge>
                    {getStatusBadge(booking.status)}
                    {booking.has_rating && (
                      <Badge variant="secondary" className="bg-yellow-500/10 text-yellow-600 border-yellow-500/20">
                        <Star className="h-3 w-3 mr-1 fill-current" />
                        {isRTL ? "تم التقييم" : "Rated"}
                      </Badge>
                    )}
                  </div>

                  <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                    {booking.budget && (
                      <span className="flex items-center gap-1">
                        <DollarSign className="h-3 w-3" />
                        {booking.budget.toLocaleString()} SAR
                      </span>
                    )}
                    {booking.preferred_start_date && (
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {new Date(booking.preferred_start_date).toLocaleDateString()}
                      </span>
                    )}
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {new Date(booking.created_at).toLocaleDateString()}
                    </span>
                  </div>

                  {booking.influencer_notes && (
                    <p className="text-sm text-muted-foreground bg-muted/50 p-2 rounded">
                      {isRTL ? "ملاحظات المؤثر: " : "Influencer notes: "}
                      {booking.influencer_notes}
                    </p>
                  )}
                </div>

                {/* Actions */}
                <div className="flex sm:flex-col gap-2 justify-end">
                  {booking.status === 'completed' && !booking.has_rating && (
                    <Button
                      size="sm"
                      onClick={() =>
                        setRatingDialog({
                          open: true,
                          bookingId: booking.id,
                          influencerId: booking.influencer.id,
                          influencerName: booking.influencer.name,
                        })
                      }
                    >
                      <Star className="h-4 w-4 mr-1" />
                      {isRTL ? "تقييم" : "Rate"}
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <RatingDialog
        open={ratingDialog.open}
        onOpenChange={(open) => setRatingDialog((prev) => ({ ...prev, open }))}
        bookingId={ratingDialog.bookingId}
        influencerId={ratingDialog.influencerId}
        influencerName={ratingDialog.influencerName}
        onRatingSubmitted={fetchBookings}
      />
    </div>
  );
};
